import { useState, useEffect } from "react";

import { useAppBridge } from "../hooks/useAppBridge";
import { useApi } from "../hooks/useApi";
import { createSettingsApi } from "../api/settings";
import { createSubscriptionApi } from "../api/subscription";
import { ContractStatusCard } from "../components/ContractStatusCard";
import { VipCodeCard } from "../components/VipCodeCard";
import { useTranslation } from "../hooks/useTranslation";

import {
  Card,
  Form,
  FormLayout,
  TextField,
  Select,
  Banner,
  Checkbox,
  SkeletonBodyText,
  SkeletonDisplayText,
  PageActions,
  Text,
  BlockStack,
  Link,
  InlineStack,
  List,
} from "@shopify/polaris";

interface SettingsPageProps {
  shop: string | null;
}

interface Settings {
  mydata_user_id: string;
  mydata_subscription_key: string;
  use_sandbox: boolean;
  issuer_vat_number: string;
  issuer_company_name: string;
  issuer_address: string;
  issuer_city: string;
  issuer_postal_code: string;
  issuer_profession: string;
  issuer_tax_office: string;
  issuer_gemi: string;
  issuer_phone: string;
  issuer_email: string;
  // Contract-specific fields (shared with onboarding)
  company_short_name: string;
  legal_representative_name: string;
  administrator_personal_vat_id: string;
  service_type: "b2b" | "b2c" | "b2b_and_b2c";
  alt_provider_name: string;
  alt_provider_contract_nr: string;
  alt_provider_contract_date: string;
  default_vat_rate: string;
  logo_url: string;
  oss_eu_enabled: boolean;
  oss_non_eu_enabled: boolean;
}

const defaultSettings: Settings = {
  mydata_user_id: "",
  mydata_subscription_key: "",
  use_sandbox: false,
  issuer_vat_number: "",
  issuer_company_name: "",
  issuer_address: "",
  issuer_city: "",
  issuer_postal_code: "",
  issuer_profession: "",
  issuer_tax_office: "",
  issuer_gemi: "",
  issuer_phone: "",
  issuer_email: "",
  company_short_name: "",
  legal_representative_name: "",
  administrator_personal_vat_id: "",
  service_type: "b2b",
  alt_provider_name: "",
  alt_provider_contract_nr: "",
  alt_provider_contract_date: "",
  default_vat_rate: "0.24",
  logo_url: "",
  oss_eu_enabled: false,
  oss_non_eu_enabled: false,
};

const SERVICE_TYPE_OPTIONS = [
  { label: "B2B (Τιμολόγηση επιχειρήσεων)", value: "b2b" },
  { label: "B2C (Λιανική)", value: "b2c" },
  { label: "B2B & B2C (Και τα δύο)", value: "b2b_and_b2c" },
];

const VAT_RATE_OPTIONS = [
  { label: "24% (Γενικός)", value: "0.24" },
  { label: "13% (Μειωμένος)", value: "0.13" },
  { label: "6% (Υπερμειωμένος)", value: "0.06" },
  { label: "9% (Νησιωτικός μειωμένος)", value: "0.09" },
  { label: "4% (Νησιωτικός υπερμειωμένος)", value: "0.04" },
  { label: "0% (Απαλλαγή ΦΠΑ)", value: "0.0" },
];

export default function SettingsPage({}: SettingsPageProps) {
  const { t } = useTranslation();
  const { app } = useAppBridge();
  const { Call } = useApi({ baseUrl: "/api" });

  const settingsApi = createSettingsApi(Call);
  const subscriptionApi = createSubscriptionApi(Call);
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  const [notification, setNotification] = useState<{
    active: boolean;
    content: string;
    status: "success" | "critical" | "warning";
  }>({
    active: false,
    content: "",
    status: "success",
  });

  useEffect(() => {
    if (!app) {
      console.log("[SettingsPage] Waiting for App Bridge...");
      return;
    }

    if (hasLoadedSettings) {
      console.log("[SettingsPage] Settings already loaded, skipping fetch");
      return;
    }

    const fetchSettings = async () => {
      console.log("[SettingsPage] App Bridge ready, fetching settings...");
      try {
        const response = await settingsApi.get();
        console.log("[SettingsPage] Get settings response:", response);

        if (response && response.success && response.data) {
          console.log(
            "[SettingsPage] Setting data from response:",
            response.data,
          );
          setSettings({
            mydata_user_id: response.data.mydata_user_id || "",
            mydata_subscription_key:
              response.data.mydata_subscription_key || "",
            use_sandbox: response.data.use_sandbox !== false,
            issuer_vat_number: response.data.issuer_vat_number || "",
            issuer_company_name: response.data.issuer_company_name || "",
            issuer_address: response.data.issuer_address || "",
            issuer_city: response.data.issuer_city || "",
            issuer_postal_code: response.data.issuer_postal_code || "",
            issuer_profession: response.data.issuer_profession || "",
            issuer_tax_office: response.data.issuer_tax_office || "",
            issuer_gemi: response.data.issuer_gemi || "",
            issuer_phone: response.data.issuer_phone || "",
            issuer_email: response.data.issuer_email || "",
            company_short_name: response.data.company_short_name || "",
            legal_representative_name: response.data.legal_representative_name || "",
            administrator_personal_vat_id: response.data.administrator_personal_vat_id || "",
            service_type: (response.data.service_type as Settings["service_type"]) || "b2b",
            alt_provider_name: response.data.alt_provider_name || "",
            alt_provider_contract_nr: response.data.alt_provider_contract_nr || "",
            alt_provider_contract_date: response.data.alt_provider_contract_date || "",
            default_vat_rate: response.data.default_vat_rate != null ? String(response.data.default_vat_rate) : "0.24",
            logo_url: response.data.logo_url || "",
            oss_eu_enabled: response.data.oss_eu_enabled || false,
            oss_non_eu_enabled: response.data.oss_non_eu_enabled || false,
          });
          setHasLoadedSettings(true);
        } else {
          console.log(
            "[SettingsPage] No data in response or response failed:",
            response,
          );
        }
      } catch (error) {
        console.error("[SettingsPage] Error fetching settings:", error);
        showNotification(t("settings.notifications.loadFailed"), "critical");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [app, hasLoadedSettings]);

  const showNotification = (
    content: string,
    status: "success" | "critical" | "warning",
  ) => {
    setNotification({
      active: true,
      content,
      status,
    });

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, active: false }));
    }, 5000);
  };

  const handleSubmit = async () => {
    // Validate required Novus fields before saving
    const errs: Record<string, string> = {};
    const req = t("validation.required");
    if (!settings.issuer_vat_number.trim()) errs.issuer_vat_number = req;
    if (!settings.issuer_company_name.trim()) errs.issuer_company_name = req;
    if (!settings.issuer_address.trim()) errs.issuer_address = req;
    if (!settings.issuer_city.trim()) errs.issuer_city = req;
    if (!settings.issuer_postal_code.trim()) errs.issuer_postal_code = req;
    if (!settings.issuer_tax_office.trim()) errs.issuer_tax_office = req;
    if (!settings.issuer_profession.trim()) errs.issuer_profession = req;
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const payload = {
        ...settings,
        default_vat_rate: parseFloat(settings.default_vat_rate),
      };
      const response = await settingsApi.save(payload);

      if (response && response.success) {
        showNotification(t("settings.notifications.saveSuccess"), "success");
      } else {
        showNotification(
          (response && response.message) ||
            t("settings.notifications.saveFailed"),
          "critical",
        );
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      showNotification(t("settings.notifications.error"), "critical");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <BlockStack gap="500">
        {/* myDATA Credentials Card Skeleton */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={1} />
            </BlockStack>
            <SkeletonBodyText lines={3} />
            <BlockStack gap="300">
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Company Information Card Skeleton */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={1} />
            </BlockStack>
            <BlockStack gap="300">
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Action Buttons Skeleton */}
        <InlineStack gap="300">
          <SkeletonDisplayText size="small" />
          <SkeletonDisplayText size="small" />
        </InlineStack>
      </BlockStack>
    );
  }

  return (
    <>
      <BlockStack gap="500">
        <ContractStatusCard />

        <VipCodeCard onRedeem={(code) => subscriptionApi.redeemPromoCode(code)} />

        {notification.active && (
          <Banner
            title={notification.status === "success" ? t("settings.notifications.bannerSuccess") : t("settings.notifications.bannerError")}
            tone={notification.status}
            onDismiss={() => setNotification((prev) => ({ ...prev, active: false }))}
          >
            <p>{notification.content}</p>
          </Banner>
        )}

        {/* Single unified form */}
        <Card>
          <BlockStack gap="500">
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">{t("settings.company.title")}</Text>
              <Text variant="bodyMd" as="p" tone="subdued">{t("settings.company.subtitle")}</Text>
            </BlockStack>

            <Form onSubmit={handleSubmit}>
              <FormLayout>

                {/* Company */}
                <Text as="h3" variant="headingSm" tone="subdued">{t("contractForm.section.companyDetails")}</Text>
                <FormLayout.Group>
                  <TextField
                    label={t("settings.company.vat.label")}
                    value={settings.issuer_vat_number}
                    onChange={(value) => setSettings({ ...settings, issuer_vat_number: value })}
                    autoComplete="off"
                    helpText={t("settings.company.vat.helpText")}
                    placeholder={t("settings.company.vat.placeholder")}
                    requiredIndicator
                    error={fieldErrors.issuer_vat_number}
                  />
                  <TextField
                    label={t("settings.company.name.label")}
                    value={settings.issuer_company_name}
                    onChange={(value) => setSettings({ ...settings, issuer_company_name: value })}
                    autoComplete="off"
                    requiredIndicator
                    error={fieldErrors.issuer_company_name}
                  />
                  <TextField
                    label={t("contractForm.fields.tradingName")}
                    value={settings.company_short_name}
                    onChange={(value) => setSettings({ ...settings, company_short_name: value })}
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <TextField
                  label={t("settings.company.address.label")}
                  value={settings.issuer_address}
                  onChange={(value) => setSettings({ ...settings, issuer_address: value })}
                  autoComplete="street-address"
                  requiredIndicator
                  error={fieldErrors.issuer_address}
                />
                <FormLayout.Group>
                  <TextField
                    label={t("settings.company.city.label")}
                    value={settings.issuer_city}
                    onChange={(value) => setSettings({ ...settings, issuer_city: value })}
                    autoComplete="address-level2"
                    requiredIndicator
                    error={fieldErrors.issuer_city}
                  />
                  <TextField
                    label={t("settings.company.postalCode.label")}
                    value={settings.issuer_postal_code}
                    onChange={(value) => setSettings({ ...settings, issuer_postal_code: value })}
                    autoComplete="postal-code"
                    placeholder={t("settings.company.postalCode.placeholder")}
                    maxLength={5}
                    requiredIndicator
                    error={fieldErrors.issuer_postal_code}
                  />
                  <TextField
                    label={t("settings.company.taxOffice.label") || "ΔΟΥ"}
                    value={settings.issuer_tax_office}
                    onChange={(value) => setSettings({ ...settings, issuer_tax_office: value })}
                    autoComplete="off"
                    requiredIndicator
                    error={fieldErrors.issuer_tax_office}
                  />
                </FormLayout.Group>
                <TextField
                  label={t("settings.company.gemi.label")}
                  value={settings.issuer_gemi}
                  onChange={(value) => setSettings({ ...settings, issuer_gemi: value })}
                  autoComplete="off"
                  helpText={t("settings.company.gemi.helpText")}
                />

                {/* Legal representative */}
                <Text as="h3" variant="headingSm" tone="subdued">{t("contractForm.section.representative")}</Text>
                <FormLayout.Group>
                  <TextField
                    label={t("contractForm.fields.representativeName")}
                    value={settings.legal_representative_name}
                    onChange={(value) => setSettings({ ...settings, legal_representative_name: value })}
                    autoComplete="name"
                  />
                  <TextField
                    label={t("contractForm.fields.representativeVat")}
                    value={settings.administrator_personal_vat_id}
                    onChange={(value) => setSettings({ ...settings, administrator_personal_vat_id: value })}
                    autoComplete="off"
                    maxLength={9}
                  />
                </FormLayout.Group>

                {/* Contact */}
                <Text as="h3" variant="headingSm" tone="subdued">{t("contractForm.section.contact")}</Text>
                <FormLayout.Group>
                  <TextField
                    label={t("contractForm.fields.contactEmail")}
                    value={settings.issuer_email}
                    onChange={(value) => setSettings({ ...settings, issuer_email: value })}
                    autoComplete="email"
                    type="email"
                    helpText={t("contractForm.fields.contactEmailHelp")}
                    placeholder="info@example.gr"
                  />
                  <TextField
                    label={t("contractForm.fields.phone")}
                    value={settings.issuer_phone}
                    onChange={(value) => setSettings({ ...settings, issuer_phone: value })}
                    autoComplete="tel"
                    placeholder="210 1234567"
                  />
                </FormLayout.Group>

                {/* Tax & branding */}
                <FormLayout.Group>
                  <TextField
                    label={t("settings.company.profession.label") || "Επάγγελμα"}
                    value={settings.issuer_profession}
                    onChange={(value) => setSettings({ ...settings, issuer_profession: value })}
                    autoComplete="off"
                    placeholder={t("settings.company.profession.placeholder") || "Λιανικό Εμπόριο"}
                    requiredIndicator
                    error={fieldErrors.issuer_profession}
                  />
                  <Select
                    label={t("settings.company.defaultVatRate.label")}
                    options={VAT_RATE_OPTIONS}
                    value={settings.default_vat_rate}
                    onChange={(value) => setSettings({ ...settings, default_vat_rate: value })}
                    helpText={t("settings.company.defaultVatRate.helpText")}
                  />
                </FormLayout.Group>

                {/* Service type */}
                <Text as="h3" variant="headingSm" tone="subdued">{t("contractForm.section.serviceType")}</Text>
                <Select
                  label={t("contractForm.fields.serviceTypeLabel")}
                  options={[
                    { label: t("contractForm.fields.serviceTypes.b2b"), value: "b2b" },
                    { label: t("contractForm.fields.serviceTypes.b2c"), value: "b2c" },
                    { label: t("contractForm.fields.serviceTypes.b2b_and_b2c"), value: "b2b_and_b2c" },
                  ]}
                  value={settings.service_type}
                  onChange={(value) =>
                    setSettings({ ...settings, service_type: value as Settings["service_type"], alt_provider_name: "", alt_provider_contract_nr: "", alt_provider_contract_date: "" })
                  }
                />

                {(settings.service_type === "b2c" || settings.service_type === "b2b_and_b2c") && (
                  <>
                    <Text as="h3" variant="headingSm" tone="subdued">{t("contractForm.section.altProvider")}</Text>
                    <Banner tone="info">
                      <p>
                        <strong>{t("contractForm.fields.b2cBannerTitle")}</strong>{" "}
                        {t("contractForm.fields.b2cBannerBody")}
                      </p>
                    </Banner>
                    <FormLayout.Group>
                      <TextField
                        label={t("contractForm.fields.altProviderName")}
                        helpText={t("contractForm.fields.altProviderNameHelp")}
                        value={settings.alt_provider_name}
                        onChange={(value) => setSettings({ ...settings, alt_provider_name: value })}
                        autoComplete="off"
                        requiredIndicator
                      />
                      <TextField
                        label={t("contractForm.fields.altProviderNr")}
                        helpText={t("contractForm.fields.altProviderNrHelp")}
                        value={settings.alt_provider_contract_nr}
                        onChange={(value) => setSettings({ ...settings, alt_provider_contract_nr: value })}
                        autoComplete="off"
                        requiredIndicator
                      />
                      <TextField
                        label={t("contractForm.fields.altProviderDate")}
                        helpText={t("contractForm.fields.altProviderDateHelp")}
                        value={settings.alt_provider_contract_date}
                        onChange={(value) => setSettings({ ...settings, alt_provider_contract_date: value })}
                        autoComplete="off"
                        type="date"
                        requiredIndicator
                      />
                    </FormLayout.Group>
                  </>
                )}


                {/* Logo */}
                <Banner tone="info">
                  <BlockStack gap="200">
                    <Text variant="bodyMd" as="p" fontWeight="semibold">{t("settings.company.logo.guideTitle")}</Text>
                    <Text variant="bodySm" as="p">{t("settings.company.logo.guideIntro")}</Text>
                    <List type="number">
                      <List.Item>{t("settings.company.logo.guideStep1")}{" "}<Link url="/admin/settings/files" external>{t("settings.company.logo.guideStep1Link")}</Link></List.Item>
                      <List.Item>{t("settings.company.logo.guideStep2")}</List.Item>
                      <List.Item>{t("settings.company.logo.guideStep3")}</List.Item>
                      <List.Item>{t("settings.company.logo.guideStep4")}</List.Item>
                    </List>
                  </BlockStack>
                </Banner>
                <TextField
                  label={t("settings.company.logo.label")}
                  value={settings.logo_url}
                  onChange={(value) => setSettings({ ...settings, logo_url: value })}
                  autoComplete="off"
                  type="url"
                  helpText={t("settings.company.logo.helpText")}
                  placeholder={t("settings.company.logo.placeholder")}
                />

              </FormLayout>
            </Form>
          </BlockStack>
        </Card>

        {/* VAT Schemes */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">{t("settings.vatSchemes.title")}</Text>
              <Text variant="bodyMd" as="p" tone="subdued">{t("settings.vatSchemes.subtitle")}</Text>
            </BlockStack>

            {/* OSS EU */}
            <BlockStack gap="200">
              <Checkbox
                label={t("settings.vatSchemes.ossEu.label")}
                checked={settings.oss_eu_enabled}
                onChange={(v) => setSettings({ ...settings, oss_eu_enabled: v })}
              />
              {settings.oss_eu_enabled && (
                <Banner tone="warning">
                  <p>{t("settings.vatSchemes.ossEu.callout")}</p>
                </Banner>
              )}
              <Text variant="bodySm" as="p" tone="subdued">{t("settings.vatSchemes.ossEu.description")}</Text>
            </BlockStack>

            {/* OSS non-EU */}
            <BlockStack gap="200">
              <Checkbox
                label={t("settings.vatSchemes.ossNonEu.label")}
                checked={settings.oss_non_eu_enabled}
                onChange={(v) => setSettings({ ...settings, oss_non_eu_enabled: v })}
              />
              {settings.oss_non_eu_enabled && (
                <Banner tone="warning">
                  <p>{t("settings.vatSchemes.ossNonEu.callout")}</p>
                </Banner>
              )}
              <Text variant="bodySm" as="p" tone="subdued">{t("settings.vatSchemes.ossNonEu.description")}</Text>
            </BlockStack>

            {/* IOSS — not yet supported */}
            <BlockStack gap="200">
              <Checkbox
                label={t("settings.vatSchemes.ioss.label")}
                checked={false}
                onChange={undefined}
                disabled
              />
              <Banner tone="info">
                <p>{t("settings.vatSchemes.ioss.notSupported")}</p>
              </Banner>
              <Text variant="bodySm" as="p" tone="subdued">{t("settings.vatSchemes.ioss.description")}</Text>
            </BlockStack>
          </BlockStack>
        </Card>

      </BlockStack>

      <PageActions
        primaryAction={{ content: t("settings.actions.save"), loading: saving, onAction: handleSubmit }}
        secondaryActions={[]}
      />
    </>
  );
}
